<template>
  <div>
    <!-- action buttons -->
    <div class="d-flex mb-4" style="gap: 10px">
      <router-link class="tab-btn btn-info" :to="`/dashboard/all-agents`">
        back to all agents
      </router-link>

      <button
        class="tab-btn btn-info"
        :to="`/dashboard/all-agents`"
        @click="refresh"
      >
        refresh
      </button>
    </div>

    <div class="add-agent">
      <!-- heading -->
      <h3 class="heading">
        {{
          currentStep === "kyc"
            ? currentStep.toUpperCase()
            : capitalize(currentStep)
        }}
        {{ currentStep === "kyc" ? "Documents" : "Information" }}
      </h3>

      <div class="row d-flex flex-wrap" style="row-gap: 15px">
        <!-- account type -->
        <div v-if="isAccountStep" class="col-md-4">
          <Select
            id="accountType"
            label="Account Type"
            :options="accountTypes"
            v-model="user.accountType"
            placeholder="Select an account"
          />
        </div>

        <!-- selected account type -->
        <div
          v-if="currentStep !== 'account' && currentStep !== 'kyc'"
          class="col-md-4 account"
        >
          <label for="account-type">Account Type</label>
          <p class="w-100 d-flex align-items-center">
            {{ capitalize(user.accountType.replace("-", " ")) }}
          </p>
        </div>

        <!-- business category -->
        <div class="col-md-4" v-if="isBusinessAccount && isAccountStep">
          <Select
            id="businessCategory"
            label="Business Category"
            :options="businessCategories"
            v-model="user.businessCategory"
            placeholder="Select a category"
          />
        </div>

        <!-- tooltip -->
        <p
          v-if="isAccountStep"
          :class="isIndividualAccount ? 'col-md-8' : ''"
          class="tool-tip mb-0 d-flex align-items-center col-md-4"
        >
          Requirements &nbsp;
          <span class="ti-info-alt" ref="requirementTooltip"></span>
        </p>

        <!-- date of birth -->
        <div
          class="col-md-4"
          v-if="(isBusinessAccount || isIndividualAccount) && isAccountStep"
        >
          <label for="inlineFormInput">Date of Birth</label>
          <div class="input-group">
            <input
              type="text"
              id="inlineFormInputGroup"
              class="form-control"
              :placeholder="`DD-MM-YYYY`"
              v-model="user.bvnPayload.dateOfBirth"
              @input="
                validateDateOfBirth(
                  $event.target.value,
                  user.bvnPayload,
                  validation.errors,
                )
              "
            />
          </div>

          <small v-if="validation.errors.dateOfBirth" class="text-danger">
            {{ validation.errors.dateOfBirth }}
          </small>
        </div>

        <!-- bvn validation -->
        <div
          class="col-md-4"
          v-if="(isBusinessAccount || isIndividualAccount) && isAccountStep"
        >
          <label for="inlineFormInput">BVN</label>
          <div class="input-group">
            <input
              type="text"
              id="inlineFormInputGroup"
              class="form-control"
              :placeholder="`Enter BVN`"
              :maxLength="11"
              v-model="user.bvnPayload.bvn"
              @input="
                handleBvnInput(
                  $event.target.value,
                  user.bvnPayload,
                  validation.errors,
                )
              "
              required
            />
          </div>

          <small v-if="validation.errors.bvn" class="text-danger">
            {{ validation.errors.bvn }}
          </small>
        </div>

        <!-- business industry -->
        <div class="col-md-4" v-if="isBusinessInformation">
          <Select
            id="businessIndustry"
            label="Business Industry"
            :options="businessIndustries"
            v-model="user.selectedBusinessInformation.businessIndustry"
            placeholder="Select an industry"
          />

          <small
            v-if="validation.errors['businessIndustry']"
            class="text-danger"
          >
            {{ validation.errors["businessIndustry"] }}
          </small>
        </div>

        <!-- company type -->
        <div class="col-md-4" v-if="isBusinessInformation">
          <Select
            id="companyType"
            label="Company Type"
            :options="filteredCompanyTypes"
            v-model="user.selectedBusinessInformation.businessType"
            placeholder="Select a company"
          />

          <small v-if="validation.errors['companyType']" class="text-danger">
            {{ validation.errors["companyType"] }}
          </small>
        </div>

        <!--partnership business -->
        <div
          class="col-md-4"
          v-if="
            isBusinessInformation && user.businessCategory === 'sole-proprietor'
          "
        >
          <Select
            id="partnershipBusiness"
            label="Partnership Business"
            :options="partnershipBusiness"
            v-model="user.selectedBusinessInformation.isPartnershipBusiness"
            placeholder="Select a partnership"
          />
        </div>
      </div>

      <!--  personal information -->
      <div v-if="currentStep === 'personal'">
        <div class="row row-wrapper">
          <div
            class="col-md-4"
            v-for="(field, index) in personalInformation"
            :key="index"
          >
            <label :for="field.key"
              >{{ field.label }}
              <span
                v-if="!field.disabled && !field.optional"
                class="text-danger"
                >*</span
              >
            </label>

            <template v-if="field.type === 'select'">
              <select
                v-model="field.value"
                :disabled="field.disabled"
                @change="handleSelectChange(field)"
                class="form-control"
              >
                <option value="">Select an option</option>
                <option
                  v-for="option in getOptions(field.key)"
                  :key="option.key"
                  :value="option.key"
                >
                  {{ option.label }}
                </option>
              </select>
            </template>

            <template v-else>
              <div
                class="position-relative d-flex justify-content-between align-items-center"
              >
                <input
                  :type="field.showPassword ? 'text' : field.type"
                  v-model="field.value"
                  :disabled="field.disabled"
                  :maxlength="
                    field.key === 'phoneNumber' || field.key === 'nin'
                      ? 11
                      : undefined
                  "
                  @input="handleInput(agent, field)"
                  class="form-control"
                  :placeholder="field.label"
                />

                <p v-if="field.key === 'nin'" class="show-icon h-100">
                  <i
                    v-if="ninStatus === 'success'"
                    class="far fa-check-square"
                  ></i>
                  <i
                    v-else-if="ninStatus === 'error'"
                    class="far fa-times-circle"
                  ></i>
                  <span
                    v-else-if="ninStatus === 'validating'"
                    class="spin"
                  ></span>
                </p>

                <p
                  class="show-icon h-100"
                  v-if="field.type === 'password'"
                  @click="togglePasswordVisibility(field)"
                >
                  <i
                    :class="
                      field.showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'
                    "
                  ></i>
                </p>
              </div>
            </template>

            <small v-if="validation.errors[field.key]" class="text-danger">
              {{ validation.errors[field.key] }}
            </small>
          </div>
        </div>

        <div class="mt-5" v-if="this.user.accountType === 'individual-account'">
          <h3 class="heading">Source of Income</h3>

          <div class="row row-wrapper">
            <div
              class="col-md-4"
              v-for="(field, index) in filteredSourceOfIncome"
              :key="index"
            >
              <label :for="field.key">{{ field.label }}</label>

              <template v-if="field.type === 'select'">
                <select v-model="field.value" class="form-control">
                  <option value="">Select an option</option>
                  <option
                    v-for="option in getOptions(field.key)"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </template>

              <small v-if="validation.errors[field.key]" class="text-danger">
                {{ validation.errors[field.key] }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!--  business information -->
      <div v-if="currentStep === 'business'">
        <!-- start of rc validation -->
        <div class="my-4 row">
          <div class="col-md-4" v-if="isBusinessInformation">
            <label for="inlineFormInput">RC Number</label>

            <input
              type="text"
              id="inlineFormInputGroup"
              class="form-control mb-0"
              v-model="rc"
              :placeholder="'Enter RC Number'"
              @input="validateRcInput"
            />

            <small v-if="validation.errors['rc']" class="text-danger">
              {{ validation.errors["rc"] }}
            </small>
          </div>

          <button
            @click="handleRcValidation"
            class="tab-btn btn-info rc-btn _btn d-flex justify-content-center align-items-center"
          >
            <span v-if="isValidatingRc" class="spin"></span> validate
          </button>
        </div>
        <!-- end of rc validation -->

        <div class="row row-wrapper">
          <div
            class="col-md-4"
            v-for="(field, index) in businessInformation"
            :key="index"
          >
            <label for="inlineFormInput">{{ field.label }}</label>

            <template v-if="field.type === 'select'">
              <select
                v-model="field.value"
                :disabled="field.disabled"
                @change="handleSelectChange(field)"
                class="form-control"
              >
                <option value="">Select an option</option>
                <option
                  v-for="option in getOptions(field.key)"
                  :key="option.key"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </template>

            <template v-else-if="field.type === 'date'">
              <input
                :type="field.type"
                v-model="field.value"
                @input="handleInput(agent, field)"
                class="form-control"
              />
            </template>

            <template v-else>
              <input
                :type="field.type || 'text'"
                v-model="field.value"
                :maxlength="
                  field.model === 'businessPhoneNumber' ? 11 : undefined
                "
                @input="handleInput(agent, field)"
                class="form-control"
              />
            </template>

            <small v-if="validation.errors[field.key]" class="text-danger">{{
              validation.errors[field.key]
            }}</small>
          </div>
        </div>

        <div class="mt-5">
          <h3 class="heading">Source of Income</h3>

          <div class="row row-wrapper">
            <div
              class="col-md-4"
              v-for="(field, index) in filteredBusinessIncome"
              :key="index"
            >
              <label :for="field.key">{{ field.label }}</label>

              <template v-if="field.type === 'select'">
                <select v-model="field.value" class="form-control">
                  <option value="">Select an option</option>
                  <option
                    v-for="option in getOptions(field.key)"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </template>

              <small v-if="validation.errors[field.key]" class="text-danger">
                {{ validation.errors[field.key] }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- directors information -->
      <div v-if="currentStep === 'directors'">
        <AgentInformation
          agentType="Directors"
          :agentList="directorsList"
          :fields="agentInformation"
          :handleBvnValidation="handleBvnValidation"
          :isValidating="isValidating"
          :isValidatingNin="isValidatingNin"
          :addNewAgent="addNewAgent"
          :validateDateOfBirth="validateDateOfBirth"
          :handleBvnInput="handleBvnInput"
          :getOptions="getOptions"
          :handleSelectChange="handleSelectChange"
          :handleInput="handleInput"
          :removeAgent="removeAgent"
        />
      </div>

      <!--  shareholders information -->
      <div v-if="currentStep === 'shareholders'">
        <AgentInformation
          agentType="Shareholders"
          :agentList="shareholdersList"
          :fields="agentInformation"
          :handleBvnValidation="handleBvnValidation"
          :isValidating="isValidating"
          :isValidatingNin="isValidatingNin"
          :addNewAgent="addNewAgent"
          :removeAgent="removeAgent"
          :validateDateOfBirth="validateDateOfBirth"
          :handleBvnInput="handleBvnInput"
          :getOptions="getOptions"
          :handleSelectChange="handleSelectChange"
          :handleInput="handleInput"
        />
      </div>

      <!--  trustees information -->
      <div v-if="currentStep === 'trustees'">
        <AgentInformation
          agentType="Trustees"
          :agentList="trusteesList"
          :fields="agentInformation"
          :handleBvnValidation="handleBvnValidation"
          :isValidating="isValidating"
          :isValidatingNin="isValidatingNin"
          :addNewAgent="addNewAgent"
          :removeAgent="removeAgent"
          :validateDateOfBirth="validateDateOfBirth"
          :handleBvnInput="handleBvnInput"
          :getOptions="getOptions"
          :handleSelectChange="handleSelectChange"
          :handleInput="handleInput"
        />
      </div>

      <!--  authorizers information -->
      <div v-if="currentStep === 'authorizers'">
        <AgentInformation
          agentType="Authorizers"
          :agentList="authorizersList"
          :fields="agentInformation"
          :handleBvnValidation="handleBvnValidation"
          :isValidating="isValidating"
          :isValidatingNin="isValidatingNin"
          :addNewAgent="addNewAgent"
          :removeAgent="removeAgent"
          :validateDateOfBirth="validateDateOfBirth"
          :handleBvnInput="handleBvnInput"
          :getOptions="getOptions"
          :handleSelectChange="handleSelectChange"
          :handleInput="handleInput"
        />
      </div>

      <!-- kyc documents -->
      <div v-if="currentStep === 'kyc'">
        <div class="row d-flex flex-wrap" style="row-gap: 15px">
          <div
            v-for="document in filteredDocuments"
            :key="document.key"
            class="col-md-4 d-flex flex-column"
            style="justify-content: end; min-height: 100px"
          >
            <label for="inlineFormInput">{{ document.name }}</label>

            <input
              :id="'fileInput_' + document.key"
              type="file"
              class="form-control d-none"
              @change="(e) => handleFileUpload(e, document.key)"
            />

            <label :for="'fileInput_' + document.key" class="file-label"
              >Choose File</label
            >
          </div>
        </div>

        <div
          v-if="uploadedDocuments.length > 0"
          class="kyc-uploaded"
          style="margin-bottom: 40px"
        >
          <h4>Uploaded Documents</h4>

          <div
            class="row d-flex flex-wrap"
            style="align-content: baseline; row-gap: 40px"
          >
            <div
              class="col-md-3"
              :key="document.key"
              v-for="document in uploadedDocuments"
            >
              <p>{{ document.name }}</p>

              <div
                v-if="
                  [
                    'image/png',
                    'image/jpg',
                    'image/jpeg',
                    'jpg',
                    'jpeg',
                    'png',
                    'jpeg',
                  ].includes(document.type)
                "
                style="
                  width: 100px;
                  height: 80px;
                  background-size: cover;
                  background-position: center;
                  background-repeat: no-repeat;
                  border-radius: 4px;
                "
                :style="{
                  backgroundImage: `url('${document.preview}')`,
                }"
              ></div>

              <img
                v-else
                src="../assets/img/pdf.png"
                alt="PDF Icon"
                style="width: 100px; height: auto"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex mt-4">
        <button
          @click="nextStep"
          :disabled="isButtonDisabled"
          class="tab-btn btn-info _btn d-flex align-items-center"
        >
          <span v-if="loading.show" class="spin"></span>
          {{ currentStep === "kyc" ? "submit" : "continue" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "../store/axios";
import Swal from "sweetalert2";
import tippy from "tippy.js";
import { mapState, mapActions } from "vuex";
import {
  steps,
  individualSteps,
  businessCategories,
  personalInformation,
  sourceOfIncome,
  businessIncome,
  annualIncome,
  occupation,
  otherOccupation,
  businessInformation,
  agentInformation,
  requiredDocuments,
  individualKyc,
  kycCategories,
  businessIndustries,
  companyTypes,
  partnershipBusiness,
  socialMedia,
  gender,
  accountTypes,
  individualTooltip,
  tooltipContent,
} from "../constants/agents-information";
import {
  capitalize,
  maskNumber,
  formatDate,
  validateBvnInput,
  handleDateOfBirthInput,
} from "../utils/utils";
import AgentInformation from "../components/AgentInformation.vue";
import Input from "../components/Inputs/Input.vue";
import Select from "../components/Select.vue";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";

export default {
  components: {
    AgentInformation,
    Input,
    Select,
    tippy,
  },
  data() {
    return {
      isValidating: false,
      isValidatingNin: false,
      isValidatingRc: false,
      loading: { show: false },
      showSuccessModal: false,
      allDocumentsUploaded: false,
      currentStepIndex: 0,
      agent: {},
      validation: {
        errors: {},
      },
      user: {
        accountType: "",
        businessCategory: "sole-proprietor",
        state: "",
        selectedBusinessInformation: {
          businessIndustry: "",
          businessType: "",
          isPartnershipBusiness: false,
        },
        bvnPayload: {
          bvn: "",
          dateOfBirth: "",
        },
      },
      bvnDetails: {
        data: {},
        status: "",
      },
      rcDetails: {
        status: "",
      },
      rc: "",
      ninStatus: "",
      placeholders: {
        account: "Select an account",
        businessCategory: "Select a business category",
      },
      mappings: {
        rcToBusinessInfo: {
          rcNumber: "rcNumber",
          companyName: "businessName",
          companyAddress: "businessAddress",
          emailAddress: "businessEmail",
          state: "businessState",
          lga: "businessLGA",
          registrationDate: "registrationDate",
        },
      },
      documentsToUpload: [],
      files: {
        requiredDocuments,
        uploadedDocuments: [],
      },
      currentSteps: [],
      // lgas: [],
      directorsList: [{ ...this.getEmptyForm() }],
      shareholdersList: [{ ...this.getEmptyForm() }],
      trusteesList: [{ ...this.getEmptyForm() }],
      authorizersList: [{ ...this.getEmptyForm() }],
      businessCategories,
      personalInformation,
      sourceOfIncome,
      businessIncome,
      annualIncome,
      occupation,
      otherOccupation,
      businessInformation,
      agentInformation,
      requiredDocuments,
      individualKyc,
      kycCategories,
      businessIndustries,
      companyTypes,
      partnershipBusiness,
      socialMedia,
      gender,
      accountTypes,
      individualTooltip,
      tooltipContent,
      tooltipInstance: null,
    };
  },
  computed: {
    ...mapState(["utils"]),
    isAccountStep() {
      return this.currentStep === "account";
    },
    isBusinessAccount() {
      return this.user.accountType === "business-account";
    },
    isIndividualAccount() {
      return this.user.accountType === "individual-account";
    },
    selectedBusinessCategoryLabel() {
      const category = this.businessCategories.find(
        (c) => c.key === this.user.businessCategory,
      );
      return category ? category.label : "";
    },
    filteredSourceOfIncome() {
      return this.sourceOfIncome.filter((field) => {
        if (
          field.key === "otherOccupation" ||
          field.key === "otherAnnualIncome"
        ) {
          return (
            this.sourceOfIncome.find((f) => f.key === "others").value === "yes"
          );
        }
        return true;
      });
    },
    filteredCompanyTypes() {
      const disabledKey =
        this.user.businessCategory === "sole-proprietor"
          ? "Registered Company (RC)"
          : this.user.businessCategory === "private-limited-liability-company"
          ? "Business Name (BN)"
          : null;

      return this.companyTypes.map((type) => ({
        ...type,
        disabled: type.key === disabledKey,
      }));
    },
    filteredBusinessIncome() {
      return this.businessIncome.filter((field) => {
        if (
          field.key === "otherIndustry" ||
          field.key === "otherAnnualIncome"
        ) {
          return (
            this.businessIncome.find((f) => f.key === "others").value === "yes"
          );
        }
        return true;
      });
    },
    isBusinessInformation() {
      return this.currentStep === "business";
    },
    isBvnSuccessful() {
      return this.bvnDetails.status === "success";
    },
    isRcSuccessful() {
      return this.rcDetails.status === "success";
    },
    filteredDocuments() {
      if (this.user.accountType === "individual-account") {
        return this.requiredDocuments.filter((doc) =>
          this.individualKyc.includes(doc.key),
        );
      } else {
        return this.documentsToUpload
          .map((key) => this.requiredDocuments.find((doc) => doc.key === key))
          .filter(Boolean);
      }
    },
    uploadedDocuments() {
      return this.filteredDocuments.filter((doc) => doc.file);
    },
    currentStep() {
      return this.currentSteps[this.currentStepIndex] || "account";
      // return "directors";
    },
    stepActions() {
      return {
        account: this.handleAccountBvnValidation,
        personal: this.addPersonalInformation,
        business: () => this.addBusiness(),
        directors: this.addDirectors,
        shareholders: this.addShareholders,
        trustees: this.addTrustees,
        authorizers: this.addNominatedAuthorizers,
        kyc: this.uploadKycDocuments,
      };
    },
    isButtonDisabled() {
      return (
        !this.user.bvnPayload.dateOfBirth ||
        !this.user.bvnPayload.bvn ||
        this.user.bvnPayload.bvn.length !== 11 ||
        !!this.validation.errors.bvn ||
        !this.user.accountType
      );
    },
  },
  methods: {
    ...mapActions(["getMerchants", "getStates", "getLgas"]),
    capitalize,
    formatDate,
    refresh() {
      window.location.reload();
    },
    initializeTooltip() {
      const tooltipElement = this.$refs.requirementTooltip;

      if (tooltipElement) {
        this.tooltipInstance = tippy(tooltipElement, {
          content: "Please select an account type",
          trigger: "mouseenter focus",
          arrow: true,
          animation: "fade",
          placement: "bottom",
          allowHTML: true,
          theme: "dark",
        });
      }
    },
    updateTooltipContent() {
      const { accountType } = this.user;

      if (!accountType) {
        this.tooltipInstance.setContent("Please select an account type");
      } else if (
        accountType === "business-account" &&
        this.tooltipContent[this.user.businessCategory]
      ) {
        this.tooltipInstance.setContent(
          this.tooltipContent[this.user.businessCategory],
        );
      } else if (accountType === "individual-account") {
        this.tooltipInstance.setContent(individualTooltip);
      }
      this.tooltipInstance.setProps({
        className: "custom-tooltip",
      });
    },
    handleInput(agent, field) {
      if (
        field.key === "phoneNumber" ||
        field.key === "businessPhoneNumber" ||
        field.key === "nin"
      ) {
        field.value = field.value.replace(/\D/g, "").slice(0, 11);
      }

      if (field.key === "rcNumber") {
        field.value = field.value.replace(/\D/g, "").slice(0);
      }

      if (
        this.currentStep === "directors" ||
        this.currentStep === "shareholders"
      ) {
        if (field.key === "nin" && agent.nin.length === 11) {
          this.validateNin(agent);
        }
      } else if (this.currentStep === "personal" && field.key === "nin") {
        agent.nin = field.value || "";

        if (agent.nin.length === 11) {
          this.$nextTick(() => {
            this.ninStatus = "validating";
            this.validateNinPersonal(field);
          });
        } else {
          this.ninStatus = null;
        }
      }

      if (field.key === "nationality") {
        agent[field.key] = agent[field.key].replace(/[^a-zA-Z\s]/g, "");
      }
    },
    isDigitValid(digit) {
      return /^\d{11}$/.test(digit);
    },
    validateDigit(field) {
      const digit = field.value;
      if (digit.length !== 11) {
        this.validation.errors[field.key] =
          "Phone number must be exactly 11 digits.";
      } else {
        this.validation.errors[field.key] = "";
      }
    },
    togglePasswordVisibility(field) {
      field.showPassword = !field.showPassword;
    },
    handleBvnInput(value, agent, validation) {
      const { isValid, errorMessage, sanitizedValue } = validateBvnInput(value);

      agent.bvn = sanitizedValue;

      if (!isValid || sanitizedValue.length !== 11) {
        validation.bvn = errorMessage;
      } else {
        delete validation.bvn;
      }
    },
    validateDateOfBirth(value, agent, validation) {
      const { formattedValue, validationError } = handleDateOfBirthInput(value);

      agent.dateOfBirth = formattedValue;
      validation.dateOfBirth = validationError;
    },
    validateRcInput(event) {
      const sanitizedValue = event.target.value.replace(/[^0-9-.]/g, "");
      this.rc = sanitizedValue;

      if (sanitizedValue.trim()) {
        this.clearRcError();
      }
    },
    clearRcError() {
      if (this.rc.trim()) {
        delete this.validation.errors["rc"];
      }
    },
    getOptions(fieldKey) {
      const optionsMap = {
        gender: gender,
        stateOfOrigin: this.utils.allStates,
        stateOfResidence: this.utils.allStates,
        lgaOfOrigin: (this.utils.lgasOfOrigin || []).map((lga) => ({
          key: lga.name,
          label: lga.name,
        })),
        lgaOfResidence: (this.utils.lgasOfResidence || []).map((lga) => ({
          key: lga.name,
          label: lga.name,
        })),
        businessState: (this.utils.allStates || []).map((state) => ({
          key: state.trim(),
          label: state.trim(),
          value: state.trim(),
        })),
        businessLGA: (this.utils.lgas || []).map((lga) => ({
          key: lga.name.trim(),
          label: lga.name.trim(),
          value: lga.name.trim(),
        })),
        socialMediaPlatform: socialMedia,
        merchantId: (this.utils.merchants || []).map((merchant) => ({
          key: merchant._id,
          label: merchant.merchantName,
        })),
        occupation: occupation,
        annualIncome: annualIncome,
        otherOccupation: occupation,
        otherIndustry: businessIndustries,
        otherAnnualIncome: annualIncome,
        others: otherOccupation,
      };

      const rawOptions = optionsMap[fieldKey] || [];

      return rawOptions.map((option) => {
        if (typeof option === "string") {
          return {
            key: option,
            label: option,
            value: option,
          };
        }

        return option;
      });
    },
    initializeSteps() {
      if (!this.user.businessCategory) {
        this.currentSteps = [];
      } else if (this.user.accountType === "individual-account") {
        this.currentSteps = individualSteps || [];
      } else if (this.user.accountType === "business-account") {
        this.currentSteps = steps[this.user.businessCategory] || [];
      }
      this.currentStepIndex = 0;
    },
    async nextStep() {
      const currentStep = this.currentStep;

      if (this.stepActions[currentStep]) {
        await this.stepActions[currentStep]();
      } else if (this.currentIndex < this.currentSteps.length - 1) {
        this.currentIndex++;
      }
    },
    prevStep() {
      if (this.currentStepIndex > 0) this.currentStepIndex--;
    },
    getEmptyForm() {
      return {
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "",
        stateOfOrigin: "",
        lgaOfOrigin: "",
        residentialAddress: "",
        nin: "",
        dateOfBirth: "",
        bvn: "",
      };
    },
    handleFileUpload(event, documentKey) {
      const file = event.target.files[0];
      if (file) {
        const document = this.filteredDocuments.find(
          (doc) => doc.key === documentKey,
        );
        document.file = file;
        document.type = file.type;
        document.preview = URL.createObjectURL(file);
        this.checkAllDocumentsUploaded();
      }
    },
    checkAllDocumentsUploaded() {
      this.allDocumentsUploaded = this.filteredDocuments.every(
        (doc) => doc.file !== null,
      );
    },
    addNewAgent(list) {
      list.push(this.getEmptyForm());
    },

    removeAgent(agentList, index) {
      agentList.splice(index, 1);
    },
    async validateBvn(payload) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/kyc/validateBvn`,
          payload,
        );

        if (response.data.status === "success") {
          this.$toast.success(response.data.message);
          return response.data;
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
      }
    },
    async validateNinPersonal(field) {
      if (!field.value) {
        console.warn("NIN value is missing!");
        return;
      }

      try {
        this.isValidatingNin = true;
        this.ninStatus = "validating";

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/onboarding/validate-nin`,
          { nin: field.value },
        );

        if (response.data.status === "success") {
          this.ninStatus = "success";
          this.$toast.success(response.data.message);
        }
      } catch (error) {
        this.ninStatus = "error";
        this.$toast.error(error.response.data.message);
      } finally {
        this.isValidatingNin = false;
      }
    },

    async validateNin(field) {
      try {
        this.isValidatingNin = true;

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/onboarding/validate-nin`,
          { nin: field.nin },
        );

        if (response.data.status === "success") {
          this.ninStatus = "success";
          this.$toast.success(response.data.message);
        }
      } catch (error) {
        this.ninStatus = "error";
        this.$toast.error(error.response.data.message);
      } finally {
        this.isValidatingNin = false;
      }
    },

    async fetchLgas(state, type = "origin") {
      if (!state) return;

      try {
        this.activeLgaType = type;

        if (type === "origin") this.utils.lgasOfOrigin = [];
        else if (type === "residence") this.utils.lgasOfResidence = [];

        this.$store.dispatch("getLgas", state);
      } catch (error) {
        console.error("Failed to fetch LGAs:", error);
      }
    },

    handleSelectChange(field) {
      if (field.key === "stateOfOrigin") {
        this.fetchLgas(field.value, "origin");
      } else if (field.key === "stateOfResidence") {
        this.fetchLgas(field.value, "residence");
      } else if (field.key === "businessState") {
        this.fetchLgas(field.value, "origin");
      }
    },
    shouldDisableField(field, value) {
      if (field.key === "middleName" || field.key === "nin") {
        return !(value === null || value === "");
      }

      return field.key !== "residentialAddress";
    },
    async handleAccountBvnValidation() {
      const { bvn, dateOfBirth } = this.user.bvnPayload || {};

      const { accountType } = this.user;

      if (!bvn || bvn.length !== 11 || !dateOfBirth || !accountType) {
        this.validation.errors.bvn = "BVN must be exactly 11 digits.";
        return;
      }

      try {
        this.loading.show = true;

        const result = await this.validateBvn(this.user.bvnPayload);
        // if (result.status !== "success") return;

        this.initializeSteps();
        this.bvnDetails = result;

        const findMatchingOption = (fieldKey, value) => {
          const options = this.getOptions(fieldKey);
          return options.find((option) =>
            [option.key, option.label].some(
              (key) =>
                key.replace(/ State$/, "").toLowerCase() ===
                value.toLowerCase(),
            ),
          );
        };

        this.personalInformation = this.personalInformation.map((field) => {
          const fieldValue = result.data[field.key] || "";
          if (!fieldValue) return field;

          if (field.key === "lgaOfOrigin")
            this.fetchLgas(result.data.stateOfOrigin, "origin");
          if (field.key === "lgaOfResidence")
            this.fetchLgas(result.data.stateOfResidence, "residence");

          const matchingOption =
            field.type === "select" ||
            [
              "stateOfOrigin",
              "lgaOfOrigin",
              "stateOfResidence",
              "lgaOfResidence",
            ].includes(field.key)
              ? findMatchingOption(field.key, fieldValue)
              : null;

          return {
            ...field,
            value: matchingOption ? matchingOption.key : fieldValue,
            disabled: this.shouldDisableField(field, fieldValue),
          };
        });
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.loading.show = false;
      }
    },
    async handleBvnValidation(agent, list, index) {
      if (!agent.bvn || !agent.dateOfBirth) {
        alert("Please provide BVN and Date of Birth");
        return;
      }

      try {
        this.isValidating = true;

        const result = await this.validateBvn({
          bvn: agent.bvn,
          dateOfBirth: agent.dateOfBirth,
        });

        if (result.status === "success") {
          const updatedData = {
            ...result.data,
            bvn: agent.bvn,
            dateOfBirth: agent.dateOfBirth,
            address: result.data.residentialAddress,
            lga: result.data.lgaOfOrigin,
            state: result.data.stateOfOrigin,
          };

          if (updatedData.nin && updatedData.nin.length === 11) {
            this.validateNin(updatedData);
          }

          delete updatedData.stateOfOrigin;
          delete updatedData.residentialAddress;
          delete updatedData.lgaOfOrigin;

          this.$set(list, index, updatedData);
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.isValidating = false;
      }
    },
    async addPersonalInformation() {
      this.validation.errors = {};
      this.personalInformation.forEach((field) => {
        if (!field.optional && !field.value.trim()) {
          this.validation.errors[field.key] = `${field.label} is required.`;
        }

        const socialMediaPlatformField = this.personalInformation.find(
          (f) => f.key === "socialMediaPlatform",
        );

        const socialMediaHandleField = this.personalInformation.find(
          (f) => f.key === "socialMediaHandle",
        );

        if (
          socialMediaPlatformField.value &&
          !socialMediaHandleField.value.trim()
        ) {
          this.validation.errors.socialMediaHandle =
            "Social Media Handle is required.";
        }
      });

      if (this.user.accountType === "individual-account") {
        const requiredFields = ["occupation", "annualIncome", "others"];

        this.sourceOfIncome.forEach((field) => {
          if (requiredFields.includes(field.key) && !field.value.trim()) {
            this.validation.errors[field.key] = `${field.label} is required.`;
          }
        });

        const othersField = this.sourceOfIncome.find((f) => f.key === "others");
        const otherSources = othersField && othersField.value === "yes";

        if (otherSources) {
          const occupationField = this.sourceOfIncome.find(
            (f) => f.key === "otherOccupation",
          );

          const incomeField = this.sourceOfIncome.find(
            (f) => f.key === "otherAnnualIncome",
          );

          if (!occupationField.value.trim()) {
            this.validation.errors["otherOccupation"] =
              "Please select an occupation.";
          }

          if (!incomeField.value.trim()) {
            this.validation.errors["otherAnnualIncome"] =
              "Please select an annual income.";
          }
        }
      }

      if (Object.keys(this.validation.errors).length > 0) return;

      const incomeSources = [];
      let tempIncome = {};

      const primaryOccupation = this.sourceOfIncome.find(
        (f) => f.key === "occupation",
      );
      const primaryIncome = this.sourceOfIncome.find(
        (f) => f.key === "annualIncome",
      );

      if (
        primaryOccupation &&
        primaryOccupation.value &&
        primaryIncome &&
        primaryIncome.value
      ) {
        incomeSources.push({
          occupation: primaryOccupation.value,
          annualIncome: primaryIncome.value,
        });
      }

      const othersField = this.sourceOfIncome.find((f) => f.key === "others");
      if (othersField && othersField.value === "yes") {
        const otherOccupation = this.sourceOfIncome.find(
          (f) => f.key === "otherOccupation",
        );
        const otherIncome = this.sourceOfIncome.find(
          (f) => f.key === "otherAnnualIncome",
        );

        if (
          otherOccupation &&
          otherOccupation.value &&
          otherIncome &&
          otherIncome.value
        ) {
          incomeSources.push({
            occupation: otherOccupation.value,
            annualIncome: otherIncome.value,
          });
        }
      }

      const passwordField = this.personalInformation.find(
        (field) => field.key === "password",
      );

      const confirmationField = this.personalInformation.find(
        (field) => field.key === "confirmation",
      );

      if (passwordField.value !== confirmationField.value) {
        this.validation.errors.confirmation = "Passwords do not match.";
      }

      if (Object.keys(this.validation.errors).length > 0) return;

      const info = this.personalInformation.reduce((acc, field) => {
        if (field.key) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});

      const payload = {
        ...info,
        ...this.user.bvnPayload,
        accountType: this.user.accountType,
        address: info.residentialAddress,
        state: info.stateOfOrigin,
        localGovernment: info.lgaOfOrigin,
        // ...(this.user.accountType === "individual-account"
        // 	? { sourcesOfIncome: incomeSources }
        // 	: {}),
        sourcesOfIncome: incomeSources,
      };

      delete payload.residentialAddress;
      delete payload.stateOfOrigin;
      delete payload.lgaOfOrigin;
      delete payload.confirmation;

      try {
        this.loading.show = true;

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/kyc/add-personal-details`,
          payload,
        );

        const { agent } = response.data;

        this.agent = agent;
        this.currentStepIndex++;
        this.$toast.success(response.data.message);
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.loading.show = false;
      }
    },
    formatDateForInput(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${year}-${month}-${day}`;
    },

    async handleRcValidation() {
      if (!this.rc.trim()) {
        this.$set(this.validation.errors, "rc", "Please enter your RC Number.");
        return;
      } else {
        this.$delete(this.validation.errors, "rc");
      }

      try {
        this.isValidatingRc = true;

        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/kyc/cac-lookup/${this.agent._id}`,
          {
            rcNumber: this.rc,
            businessType: this.user.selectedBusinessInformation.businessType,
          },
        );

        if (res.data.status === "success") {
          this.rcDetails = res.data;
          const rcData = res.data.data;

          const findMatchingOption = (fieldKey, value) => {
            const options = this.getOptions(fieldKey);

            const normalize = (str) =>
              (str || "").replace(/[^a-zA-Z]/g, "").toLowerCase();

            const cleanValue = normalize(value);

            return options.find((option) => {
              const optionKey = normalize(option.key);
              const optionLabel = normalize(option.label);

              return (
                optionKey === cleanValue ||
                optionLabel === cleanValue ||
                optionKey.includes(cleanValue) ||
                optionLabel.includes(cleanValue)
              );
            });
          };

          let selectedState = null;

          this.businessInformation.forEach((field) => {
            Object.entries(this.mappings.rcToBusinessInfo).forEach(
              ([tinKey, businessKey]) => {
                if (field.key === businessKey && rcData[tinKey]) {
                  let value = rcData[tinKey];

                  if (
                    field.type === "select" &&
                    field.key === "businessState"
                  ) {
                    const match = findMatchingOption("businessState", value);
                    value = match ? match.value : value;
                    selectedState = value;
                  }

                  field.value = value;
                }
              },
            );
          });

          if (selectedState) {
            await this.fetchLgas(selectedState);
          }

          this.businessInformation.forEach((field) => {
            const fieldValue = field.value;

            if (fieldValue) {
              if (field.type === "date" && field.key === "registrationDate") {
                field.value = this.formatDateForInput(fieldValue);
              } else if (
                field.type === "select" &&
                ["businessState", "businessLGA"].includes(field.key)
              ) {
                const matchingOption = findMatchingOption(
                  field.key,
                  fieldValue,
                );
                field.value = matchingOption ? matchingOption.key : fieldValue;
              } else {
                field.value = fieldValue;
              }
            }
          });
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.isValidatingRc = false;
      }
    },

    async addBusiness() {
      this.validation.errors = {};

      const requiredFields = ["annualIncome"];
      let selectedOthers = false;

      this.businessIncome.forEach((field) => {
        if (requiredFields.includes(field.key) && !field.value.trim()) {
          this.validation.errors[field.key] = `${field.label} is required.`;
        }

        if (field.key === "others" && field.value) {
          selectedOthers = true;
        }
      });

      if (selectedOthers) {
        const industryField = this.businessIncome.find(
          (f) => f.key === "otherIndustry",
        );

        const incomeField = this.businessIncome.find(
          (f) => f.key === "otherAnnualIncome",
        );

        if (!industryField.value.trim()) {
          this.validation.errors["otherIndustry"] =
            "Please select an industry.";
        }

        if (!incomeField.value.trim()) {
          this.validation.errors["otherAnnualIncome"] =
            "Please select an annual income.";
        }
      }

      if (!this.user.selectedBusinessInformation.businessIndustry.trim()) {
        this.validation.errors["businessIndustry"] =
          "Business Industry is required.";
      }

      if (!this.user.selectedBusinessInformation.businessType.trim()) {
        this.validation.errors["companyType"] = "Company Type is required.";
      }

      if (
        !this.user.selectedBusinessInformation.businessLGA ||
        !this.user.selectedBusinessInformation.businessLGA.trim()
      ) {
        this.validation.errors["businessLGA"] = "Business LGA is required.";
      }

      if (
        !this.user.selectedBusinessInformation.businessState ||
        !this.user.selectedBusinessInformation.businessState.trim()
      ) {
        this.validation.errors["businessState"] = "Business State is required.";
      }

      // if (Object.keys(this.validation.errors).length > 0) return;

      const incomeSources = [];

      let tempIncome = {};

      this.businessIncome.forEach((field) => {
        if (field.key === "annualIncome") {
          tempIncome[field.key] = field.value;

          if (tempIncome.annualIncome) {
            incomeSources.push({ ...tempIncome });
            tempIncome = {};
          }
        }

        if (field.key === "otherIndustry") {
          tempIncome.industry = field.value;
        }

        if (field.key === "otherAnnualIncome") {
          tempIncome.annualIncome = field.value;
        }

        if (
          tempIncome.industry &&
          tempIncome.annualIncome &&
          !incomeSources.some((item) => item.industry === tempIncome.industry)
        ) {
          incomeSources.push({ ...tempIncome });
          tempIncome = {};
        }
      });

      const businessData = this.businessInformation.reduce((acc, field) => {
        if (field.key) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {});

      const payload = {
        ...businessData,
        ...this.user.selectedBusinessInformation,
        // tin: this.rc,
        businessCategory: this.user.businessCategory,
        sourcesOfIncome: incomeSources,
        isPartnershipBusiness:
          this.user.selectedBusinessInformation.isPartnershipBusiness,
      };

      try {
        this.loading.show = true;

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/kyc/addBusinessInformation/${this.agent._id}`,
          payload,
        );
        if (response.data.status === "success") {
          this.documentsToUpload = response.data.documentsToUpload;
          this.currentStepIndex++;
        }
      } catch (error) {
        const errors = error.response.data.errors;

        if (errors) {
          Object.keys(errors).forEach((field) => {
            errors[field].forEach((msg) => {
              this.$toast.error(`${msg}`);
            });
          });
        } else {
          this.$toast.error(error.response.data.message);
        }
      } finally {
        this.loading.show = false;
      }
    },
    async addEntity(title, list, endpointKey) {
      this.loading.show = true;

      try {
        const payload = {
          [title]: [...this[list]],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/kyc/${endpointKey}/${this.agent._id}`,
          payload,
        );

        if (response.data.status === "success") this.currentStepIndex++;
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.loading.show = false;
      }
    },
    async addDirectors() {
      await this.addEntity("directors", "directorsList", "addDirectors");
    },
    async addShareholders() {
      await this.addEntity(
        "shareholders",
        "shareholdersList",
        "addShareholders",
      );
    },
    async addTrustees() {
      await this.addEntity(
        "registeredTrustees",
        "trusteesList",
        "addRegisteredTrustees",
      );
    },
    async addNominatedAuthorizers() {
      await this.addEntity(
        "nominatedAuthorizers",
        "authorizersList",
        "addNominatedAuthorizers",
      );
    },
    async uploadKycDocuments() {
      const formData = new FormData();

      this.filteredDocuments.forEach((doc) => {
        if (doc.file) {
          formData.append(doc.key, doc.file);
        }
      });

      const apiUrl =
        this.user.accountType === "individual-account"
          ? `${process.env.VUE_APP_API_URL}/kyc/update-kyc-docs/${this.agent._id}`
          : `${process.env.VUE_APP_API_URL}/kyc/upload-document/${this.agent._id}`;

      try {
        this.loading.show = true;

        const response = await axios({
          method:
            this.user.accountType === "individual-account" ? "put" : "post",
          url: apiUrl,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        const { status } = response.data;

        if (status === "success") {
          Swal.fire({
            icon: status,
            title: "ACCOUNT CREATED",
            html: `<p><strong>${this.agent.firstName} ${this.agent.lastName}</strong> with assigned Wallet ID: <strong>${this.agent.walletId}</strong> account has been created.</p>`,
            confirmButtonText: "Proceed to all agents",
            showCancelButton: false,
          }).then(() => {
            this.$router.push({ name: "All Agents" });
            this.refresh();
          });
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.loading.show = false;
      }
    },
  },
  async mounted() {
    this.getMerchants();
    this.getStates().then((states) => (this.utils.allStates = states));
    this.initializeTooltip();
  },
  watch: {
    "user.accountType"(newAccountType) {
      this.updateTooltipContent();
    },

    "user.businessCategory"(newValue) {
      this.updateTooltipContent();
    },
    "agent.nin"(newVal) {
      if (newVal && newVal.length === 11) {
        this.validateNin(this.agent);
      }
    },

    "utils.lgas"(newLgas) {
      if (this.activeLgaType === "origin") {
        this.utils.lgasOfOrigin = newLgas;
      } else if (this.activeLgaType === "residence") {
        this.utils.lgasOfResidence = newLgas;
      }
    },
  },
};
</script>

<style scoped>
.tool-tip {
  font-size: 12px;
}

label {
  color: #9a9a9a;
  font-size: 10px;
  white-space: nowrap;
}

input,
select {
  background-color: #fafafa;
  font-size: 10px;
}

input::placeholder,
select::placeholder {
  color: #b6b6b4 !important;
}

input[type="file"] {
  display: none;
}

input:disabled,
select:disabled {
  background-color: #f5f5f5;
  color: #71797e;
}

.add-agent {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
}

.heading {
  font-size: 20px;
  color: #000000;
  font-weight: 700;
  margin-bottom: 20px;
  margin-top: 0 !important;
}

.row-wrapper {
  row-gap: 20px;
  margin-top: 30px;
}

.show-icon {
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  right: 3%;
}

._btn {
  gap: 8px;
  height: 40px;
  height: 40px !important;
  margin-top: 24px;
}

.rc-btn {
  width: fit-content;
}

.view-more {
  display: block;
  margin: 20px 0;
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #1eb75b;
  text-transform: capitalize;
  cursor: pointer;
  letter-spacing: 0.1px;
}

.file-label {
  background-color: #f4f4f4;
  color: #66615c;
  border: 1px solid #eaeaea;
  padding: 10px 10px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 4px;
}

.file-label:hover {
  background-color: #f4f4f4;
}

.kyc-uploaded h4 {
  color: #000;
  font-size: 20px;
  font-weight: 700 !important;
  margin-bottom: 30px;
}

.kyc-uploaded p {
  color: #000;
  font-size: 12px;
  font-weight: 600 !important;
}

.kyc-uploaded img {
  width: 100px;
  height: 100px;
}

.step-btn {
  font-size: 10px;
  padding: 6px 20px;
  letter-spacing: 1px;
}

.next-btn {
  gap: 8px;
}

.spin {
  border: 2px solid rgba(101, 179, 199, 0.2);
  border-left-color: #65b3c7;
  width: 15px;
  height: 15px;
}
</style>
